/**
 * The Discover section catalog and its presets (#2292), from the server's
 * registry (#2298).
 *
 * The server (`DiscoverSections.java`) owns the section types, their
 * variants and settings, the presets and the limits, and serves them on
 * every answer of `preferences/discover` (`DiscoverLayout.registry`). Each
 * lookup here reads a registry: the one the document came with
 * (`registryOf(layout)`), or this build's copy (`BUILT_IN_REGISTRY`) before
 * the first answer. So two builds draw a preset the same way, and a type the
 * server adds to a preset reaches every page on it.
 *
 * What stays here is this build's own: which types it can draw (a type the
 * server serves and this build cannot draw is kept as it came, never drawn),
 * and the words for them (labels, blurbs).
 */
import {
  BUILT_IN_REGISTRY,
  type DiscoverLayout,
  type FilterBoxMode,
  type DiscoverPreset,
  type DiscoverRegistry,
  type DiscoverSection,
  type DiscoverSectionType,
  type DiscoverVariant,
} from '@/api/discoverPreferences';
import type { FeaturedSource, FavoriteKind } from '../favorites';

/** The section types a person adds from the catalog: every type but a saved search. */
export type BuiltInType = Exclude<DiscoverSectionType, 'savedSearch'>;

/** The registry a document was answered with; this build's copy for one without (the first frame, or an older cache). */
export function registryOf(layout?: Pick<DiscoverLayout, 'registry'> | null): DiscoverRegistry {
  return layout?.registry ?? BUILT_IN_REGISTRY;
}

/** Each type this build draws, in words: one line for "+ Add section". A saved search is named by its search. */
const TYPE_COPY: Record<BuiltInType, { label: string; blurb: string }> = {
  featured: { label: 'Featured', blurb: 'A large rotating banner of featured models.' },
  trending: { label: 'Trending now', blurb: 'What the community ran most this week.' },
  favorites: { label: 'Your favorites', blurb: 'The models and functionals you marked.' },
  editorsPicks: { label: "Editor's picks", blurb: 'Hand-curated models the banner is not showing.' },
  continueEditing: { label: 'Continue editing', blurb: 'The models you last saved or ran.' },
  scheduledTasks: { label: 'My scheduled tasks', blurb: 'Your tasks, and how each last ran.' },
  sharedWithMe: { label: 'Shared with me', blurb: 'Models colleagues shared with you, latest updated first.' },
};

/** The types this build has a renderer for. */
const DRAWS: ReadonlySet<string> = new Set<string>([...Object.keys(TYPE_COPY), 'savedSearch']);

/** Every type this build can draw, whatever a registry serves: what `DiscoverSectionType` names. */
export const DRAWN_TYPES: readonly DiscoverSectionType[] = [...DRAWS] as DiscoverSectionType[];

/** A type the registry accepts and this build draws. */
export function drawsType(type: string, registry: DiscoverRegistry = BUILT_IN_REGISTRY): type is DiscoverSectionType {
  return DRAWS.has(type) && registry.types.some((t) => t.type === type);
}

/** The variants a type may be drawn as, the first its fallback; none for a type the registry does not have. */
export function variantsOf(type: string, registry: DiscoverRegistry = BUILT_IN_REGISTRY): readonly DiscoverVariant[] {
  return (registry.types.find((t) => t.type === type)?.variants ?? []) as DiscoverVariant[];
}

/** The values a `oneOrMoreOf` setting may hold, in the registry's order; none when the type has no such setting. */
export function allowedOf(type: string, key: string, registry: DiscoverRegistry = BUILT_IN_REGISTRY): readonly string[] {
  const rule = registry.types.find((t) => t.type === type)?.settings[key];
  return rule && 'oneOrMoreOf' in rule ? rule.oneOrMoreOf : [];
}

/** The values a `oneOf` setting may be, in the registry's order; none when the type has no such setting. */
export function choicesOf(type: string, key: string, registry: DiscoverRegistry = BUILT_IN_REGISTRY): readonly string[] {
  const rule = registry.types.find((t) => t.type === type)?.settings[key];
  return rule && 'oneOf' in rule ? rule.oneOf : [];
}

export interface CatalogEntry {
  type: BuiltInType;
  label: string;
  /** One line for "+ Add section". */
  blurb: string;
  /** The section a new one of this type starts as: the default preset's, shown. */
  section: DiscoverSection;
}

/** The variants a type may be drawn as, in words ("Show as"). */
export const VARIANT_LABELS: Record<DiscoverVariant, string> = {
  hero: 'Banner',
  carousel: 'Shelf',
  grid: 'Grid',
  list: 'List',
};

/** A saved search's filter box settings (#2302), in words. */
export const FILTER_BOX_LABELS: Record<FilterBoxMode, string> = {
  auto: 'Auto',
  on: 'Always on',
  off: 'Always off',
};

/** The featured banner's sources, in words, as `featuredItems` reads them. */
export const FEATURED_SOURCES: { value: FeaturedSource; label: string }[] = [
  { value: 'picks', label: "Editor's picks" },
  { value: 'mixed', label: 'Mixed' },
  { value: 'favorites', label: 'Favorites only' },
];

/** `settings.sources` for each featured source. */
export const SOURCES_FOR: Record<FeaturedSource, ('editorsPicks' | 'favorites')[]> = {
  picks: ['editorsPicks'],
  mixed: ['editorsPicks', 'favorites'],
  favorites: ['favorites'],
};

/**
 * The kinds the favorites section offers, of those the registry allows.
 * Nothing in the app can favorite a document, so the sheet offers models and
 * functionals only, although the server's registry (and the default's
 * `kinds`) still allows "document" (#2298 kept it, for a later way to
 * favorite documents).
 */
export const FAVORITE_KINDS: { value: FavoriteKind; label: string }[] = [
  { value: 'model', label: 'Models' },
  { value: 'functional', label: 'Functionals' },
];

export type NamedPreset = Exclude<DiscoverPreset, 'custom'>;

export interface PresetDefinition {
  /** A name the server serves: one of this build's, or a newer server's. */
  name: NamedPreset | (string & {});
  label: string;
  blurb: string;
  sections: readonly DiscoverSection[];
}

/** Each preset in words. A preset the server serves and this build has no words for is named by its name. */
const PRESET_COPY: Record<DiscoverPreset, { label: string; blurb: string }> = {
  default: { label: 'Default', blurb: 'Featured models, trending, favorites and picks.' },
  explorer: { label: 'Explorer', blurb: "What's new and popular across brewlytics." },
  builder: { label: 'Builder', blurb: 'Your own work first: recent models and favorites.' },
  operator: { label: 'Operator', blurb: 'The models you run, at a glance.' },
  custom: { label: 'Custom', blurb: 'Your own arrangement.' },
};

export const PRESET_LABELS: Record<DiscoverPreset, string> = Object.fromEntries(
  Object.entries(PRESET_COPY).map(([name, copy]) => [name, copy.label]),
) as Record<DiscoverPreset, string>;

function presetCopy(name: string): { label: string; blurb: string } {
  return PRESET_COPY[name as DiscoverPreset] ?? { label: name.charAt(0).toUpperCase() + name.slice(1), blurb: '' };
}

interface Derived {
  catalog: readonly CatalogEntry[];
  presets: readonly PresetDefinition[];
}

/** What each registry comes to, worked out once per registry (an answer's is a new object). */
const DERIVED = new WeakMap<DiscoverRegistry, Derived>();

function derived(registry: DiscoverRegistry): Derived {
  let d = DERIVED.get(registry);
  if (!d) {
    const presets: PresetDefinition[] = registry.presets.map((p) => ({ name: p.name, ...presetCopy(p.name), sections: p.sections }));
    const defaults = registry.presets.find((p) => p.name === 'default')?.sections ?? [];
    const catalog: CatalogEntry[] = [];
    for (const { type } of registry.types) {
      const copy = TYPE_COPY[type as BuiltInType];
      const section = defaults.find((s) => s.type === type);
      if (copy && section) catalog.push({ type: type as BuiltInType, ...copy, section: { ...section, hidden: false } });
    }
    d = { catalog, presets };
    DERIVED.set(registry, d);
  }
  return d;
}

/** The types a person adds from the catalog, in registry order: each the registry has and this build draws. */
export function catalogOf(registry: DiscoverRegistry = BUILT_IN_REGISTRY): readonly CatalogEntry[] {
  return derived(registry).catalog;
}

export function catalogEntry(type: string, registry: DiscoverRegistry = BUILT_IN_REGISTRY): CatalogEntry | undefined {
  return derived(registry).catalog.find((e) => e.type === type);
}

/** The presets, in the order the sheet offers them. `default` is the built-in default page. */
export function presetsOf(registry: DiscoverRegistry = BUILT_IN_REGISTRY): readonly PresetDefinition[] {
  return derived(registry).presets;
}

/** A named preset's definition; none for `custom` or a name the registry does not serve. */
export function presetOf(name: string, registry: DiscoverRegistry = BUILT_IN_REGISTRY): PresetDefinition | undefined {
  return derived(registry).presets.find((p) => p.name === name);
}
